import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cookie consent banner component
export function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  
  useEffect(() => {
    const consent = localStorage.getItem('filmcable_cookie_consent');
    if (!consent) {
      // Show banner after a short delay so it doesn't block initial view
      const timer = setTimeout(() => setShowBanner(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);
  
  const acceptCookies = () => {
    localStorage.setItem('filmcable_cookie_consent', 'accepted');
    localStorage.setItem('filmcable_cookie_consent_date', new Date().toISOString());
    setShowBanner(false);
  };
  
  const declineCookies = () => {
    localStorage.setItem('filmcable_cookie_consent', 'declined');
    localStorage.setItem('filmcable_cookie_consent_date', new Date().toISOString());
    setShowBanner(false);
  };
  
  if (!showBanner) return null;
  
  return (
    <div 
      className="fixed bottom-0 left-0 right-0 bg-white border-t border-zinc-200 p-4 md:p-6 shadow-lg z-50"
      data-testid="cookie-consent-banner"
    >
      <div className="container mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="text-sm text-zinc-600 text-center md:text-left">
          <p>
            We use cookies and similar technologies to enhance your browsing experience, 
            analyze site traffic, and understand where our audience comes from. 
            By continuing to use our site, you consent to our use of cookies.
          </p>
        </div>
        <div className="flex gap-3 shrink-0">
          <button
            onClick={declineCookies}
            className="px-4 py-2 text-sm font-medium text-zinc-600 hover:text-zinc-900 border border-zinc-300 hover:border-zinc-400 transition-colors"
            data-testid="decline-cookies-btn"
          >
            Decline
          </button>
          <button
            onClick={acceptCookies}
            className="px-4 py-2 text-sm font-bold text-white bg-[#FF66C4] hover:bg-[#e55ab0] transition-colors"
            data-testid="accept-cookies-btn"
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
}

// Visitor tracking hook - tracks ALL page views with idempotency guard
export function useVisitorTracking() {
  const location = useLocation();
  const trackedPaths = useRef(new Set());
  
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Idempotency guard - mark as tracked BEFORE async request to prevent StrictMode duplicates
    if (trackedPaths.current.has(currentPath)) {
      return;
    }
    
    // Mark path as tracked immediately (before async operation)
    trackedPaths.current.add(currentPath);
    
    const trackVisit = async () => {
      try {
        const trackData = {
          page_url: currentPath,
          page_title: document.title,
          referrer: document.referrer || null,
          user_agent: navigator.userAgent,
          screen_width: window.screen.width,
          screen_height: window.screen.height
        };
        
        await fetch(`${API}/track`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(trackData),
          credentials: 'include'  // Important: include cookies for session tracking
        });
      } catch (error) {
        // On failure, remove from tracked so it can retry on next navigation
        trackedPaths.current.delete(currentPath);
        console.debug('Tracking failed:', error);
      }
    };
    
    trackVisit();
  }, [location.pathname]);
}

// Wrapper component that includes tracking
export function VisitorTracker({ children }) {
  useVisitorTracking();
  return children;
}

export default CookieConsent;
